// Foto de perfil de una persona, con el mismo aspecto en toda la app: redonda, fondo
// azul suave y las iniciales cuando no hay foto. Se extrajo porque el mismo bloque
// (foto → iniciales → icono) estaba repitiéndose en cada ficha.

import { useState, useEffect } from "react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFotoOffline } from "@/lib/offline/fotosOffline";

/**
 * Iniciales de un nombre: la primera letra de las dos primeras palabras.
 *
 * Ojo: el helper local de Teams/TeamMemberDetailDrawer hace `.split("")`, que parte por
 * CARACTERES, así que en realidad devuelve las dos primeras letras del nombre ("Ana Ruiz"
 * → "AN"). Aquí se hacen las iniciales de verdad ("Ana Ruiz" → "AR"); para un nombre de
 * una sola palabra ambos coinciden. No se toca el de Teams para no cambiarle el aspecto.
 */
export function personInitials(name: string | null | undefined): string {
  if (!name || !name.trim()) return "??";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

interface PersonAvatarProps {
  name?: string | null;
  avatarUrl?: string | null;
  /** Lado del círculo en píxeles (por defecto 36 = w-9 h-9). */
  size?: number;
  className?: string;
}

export function PersonAvatar({ name, avatarUrl, size = 36, className }: PersonAvatarProps) {
  const initials = name?.trim() ? personInitials(name) : null;
  /* Si la foto no carga (enlace caducado, borrada del almacenamiento, sin red) se
     enseñan las iniciales, no el icono de imagen rota. Se reintenta cuando cambia
     la url: si no, una vez fallada quedaba marcada para siempre. */
  const [imgFailed, setImgFailed] = useState(false);
  // La copia del teléfono si la hay (modo sin conexión); si no, la de siempre.
  const foto = useFotoOffline(avatarUrl);
  /* Se reintenta cuando cambia lo que se pinta, no solo la url de origen: con
     red mala la de internet puede fallar justo antes de que llegue la guardada. */
  useEffect(() => { setImgFailed(false); }, [foto]);
  const showImg = !!foto && !imgFailed;
  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center shrink-0 overflow-hidden",
        "font-bold text-blue-700",
        className,
      )}
      style={{ width: size, height: size, backgroundColor: "#DBEAFE", fontSize: Math.round(size * 0.38) }}
    >
      {showImg ? (
        <img
          src={foto}
          alt={name || ""}
          className="w-full h-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : initials ? (
        initials
      ) : (
        <User className="text-blue-700" style={{ width: size * 0.45, height: size * 0.45 }} />
      )}
    </div>
  );
}
